/**
 * P6: 主管审批 sales 申请创建客户
 *
 * 3 tab：待审批 / 已同意 / 已拒绝
 * pending 显示 24h 倒计时；超时由 cron 自动通过
 */
import { useState } from 'react';
import { Modal, Input, Tabs, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';

const SOURCE_MAP: Record<string, string> = {
  xiaohongshu: '小红书', douyin: '抖音', shipinhao: '视频号', baidu: '百度', other: '其他',
};
const DEMAND_MAP: Record<string, string> = {
  work_cert: '🎯 工作考证',
  fulltime_shop: '🏪 全职做店铺',
  sideline_shop: '💰 副业做店铺',
  brand_overseas: '🌍 品牌出海',
};

export function CustomerCreationRequestsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pending');
  const [rejecting, setRejecting] = useState<any>(null);
  const [rejectComment, setRejectComment] = useState('');
  // ux1：同意按钮 actingId 精确防重（Btn 仅支持 disabled）——只禁正在处理的那一行，非整列齐禁
  const [approvingId, setApprovingId] = useState<number | null>(null);

  const { data: rows } = useQuery<any[]>({
    queryKey: ['customer-creation-requests', tab],
    queryFn: () => $.get(`/customer-creation-requests?status=${tab}`),
    refetchInterval: 30_000,
  });

  const approve = useMutation({
    mutationFn: (id: number) => $.post(`/customer-creation-requests/${id}/approve`, {}),
    onMutate: (id: number) => { setApprovingId(id); },
    onSuccess: () => {
      message.success('已同意，客户已自动创建');
      qc.invalidateQueries({ queryKey: ['customer-creation-requests'] });
    },
    onError: (e: any) => message.error(e?.message ?? '操作失败'),
    onSettled: () => { setApprovingId(null); },
  });
  const reject = useMutation({
    mutationFn: () => $.post(`/customer-creation-requests/${rejecting.id}/reject`, { comment: rejectComment }),
    onSuccess: () => {
      message.success('已拒绝');
      setRejecting(null); setRejectComment('');
      qc.invalidateQueries({ queryKey: ['customer-creation-requests'] });
    },
    onError: (e: any) => message.error(e?.message ?? '操作失败'),
  });

  const { data: usersResp } = useQuery<any>({
    queryKey: ['users-for-requests'],
    queryFn: () => $.get('/users?pageSize=200'),
  });
  const userMap = new Map<number, string>();
  (usersResp?.items ?? usersResp ?? []).forEach((u: any) => userMap.set(Number(u.id), u.realName));

  return (
    <>
      <PageHeader
        breadcrumb={['主管', '客户申请']}
        title="📋 销售员申请创建客户"
        subtitle="销售员自加微信客户需经主管审批，超 24h 未审批系统自动通过"
      />
      <div className="work-content">
        <Tabs activeKey={tab} onChange={setTab}
              items={[
                { key: 'pending',       label: `待审批（${rows?.length ?? 0}）` },
                { key: 'approved',      label: '已同意' },
                { key: 'rejected',      label: '已拒绝' },
                { key: 'auto_approved', label: '系统自动通过' },
              ]} />
        <HomeCard title="申请列表">
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>申请人</th>
                  <th>客户姓名</th>
                  <th>微信 / 手机</th>
                  <th>来源</th>
                  <th>需求类型</th>
                  <th>理由</th>
                  <th>申请时间</th>
                  {tab === 'pending' && <th>剩余</th>}
                  {tab === 'pending' && <th>操作</th>}
                  {tab !== 'pending' && <th>审批结果</th>}
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无</td></tr>
                )}
                {(rows ?? []).map((r: any) => {
                  const elapsed = (Date.now() - new Date(r.createdAt).getTime()) / 3_600_000;
                  const remaining = Math.max(0, 24 - elapsed);
                  const tone: any = remaining < 4 ? 'danger' : remaining < 12 ? 'warning' : 'success';
                  return (
                    <tr key={r.id}>
                      <td>{userMap.get(Number(r.requesterUserId)) ?? `#${r.requesterUserId}`}</td>
                      <td><strong>{r.customerName}</strong> {r.wechatNickname && <span style={{ color: 'var(--text-3)', fontSize: 11 }}>({r.wechatNickname})</span>}</td>
                      <td>
                        {r.wechatId && <div style={{ fontSize: 12 }}>{r.wechatId}</div>}
                        {r.phone && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.phone}</div>}
                      </td>
                      <td><Pill tone="default">{SOURCE_MAP[r.source] ?? r.source ?? '—'}</Pill></td>
                      <td>{DEMAND_MAP[r.demandType] ?? '—'}</td>
                      <td style={{ maxWidth: 200, whiteSpace: 'normal', fontSize: 12 }}>{r.reason ?? '—'}</td>
                      <td>{dayjs(r.createdAt).format('MM-DD HH:mm')}</td>
                      {tab === 'pending' && (
                        <td><Pill tone={tone}>{remaining.toFixed(1)}h</Pill></td>
                      )}
                      {tab === 'pending' && (
                        <td>
                          <Btn kind="primary" onClick={() => approve.mutate(r.id)} disabled={approvingId === r.id}>{approvingId === r.id ? '处理中…' : '✓ 同意'}</Btn>
                          <Btn kind="default" onClick={() => setRejecting(r)}>✗ 拒绝</Btn>
                        </td>
                      )}
                      {tab !== 'pending' && (
                        <td>
                          <Pill tone={r.status === 'rejected' ? 'danger' : 'success'}>{r.status}</Pill>
                          {r.reviewComment && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{r.reviewComment}</div>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <Modal
        title="拒绝客户申请"
        open={!!rejecting}
        onCancel={() => { setRejecting(null); setRejectComment(''); }}
        footer={
          <>
            <Btn kind="default" onClick={() => { setRejecting(null); setRejectComment(''); }}>取消</Btn>
            <Btn kind="primary" onClick={() => reject.mutate()}
                 disabled={!rejectComment.trim() || reject.isPending}>
              {reject.isPending ? '提交中…' : '确认拒绝'}
            </Btn>
          </>
        }
      >
        <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-2)' }}>
          拒绝必填理由，将通过 IM 通知销售员
        </div>
        <Input.TextArea value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} rows={4}
                        placeholder="如：客户已在投流系统 / 资料不全 / 等" />
      </Modal>
    </>
  );
}
